import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";

const MAX_POLLS = 8;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let polls = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setPlan(data.plan);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
        if (polls >= MAX_POLLS) { setState("pending"); return; }
        polls += 1;
        timer = setTimeout(poll, 2000);
      } catch { setState("error"); }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background text-foreground grid place-items-center p-6">
      <div className="max-w-md w-full border border-border bg-card p-8 text-center" data-testid="payment-success-page">
        <div className="mb-6"><Logo /></div>
        {state === "checking" && (<><Loader2 className="w-12 h-12 text-brand animate-spin mx-auto mb-4" /><h1 className="font-serif text-2xl font-semibold">Confirming your payment…</h1><p className="text-sm text-muted-foreground mt-2">This only takes a moment.</p></>)}
        {state === "paid" && (<>
          <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-4" data-testid="payment-success-icon" />
          <h1 className="font-serif text-2xl font-semibold">Payment successful</h1>
          <p className="text-sm text-muted-foreground mt-2">Your workspace is now on the {plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : "new"} plan. All its features are unlocked.</p>
          <Link to="/app/billing"><Button className="rounded-none mt-6 w-full" data-testid="payment-success-continue">Go to my workspace <ArrowRight className="w-4 h-4 ml-1.5" /></Button></Link>
        </>)}
        {state === "pending" && (<>
          <Loader2 className="w-12 h-12 text-brand mx-auto mb-4" />
          <h1 className="font-serif text-2xl font-semibold">Payment processing</h1>
          <p className="text-sm text-muted-foreground mt-2">Your payment is still being confirmed. Your plan will update automatically once it clears.</p>
          <Link to="/app/billing"><Button variant="outline" className="rounded-none mt-6 w-full">Back to billing</Button></Link>
        </>)}
        {state === "error" && (<>
          <XCircle className="w-14 h-14 text-destructive mx-auto mb-4" />
          <h1 className="font-serif text-2xl font-semibold">We couldn't confirm this payment</h1>
          <p className="text-sm text-muted-foreground mt-2">If you were charged, your plan will update shortly. Otherwise please try again.</p>
          <Link to="/app/billing"><Button variant="outline" className="rounded-none mt-6 w-full">Back to billing</Button></Link>
        </>)}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen bg-background text-foreground grid place-items-center p-6">
      <div className="max-w-md w-full border border-border bg-card p-8 text-center" data-testid="payment-cancel-page">
        <div className="mb-6"><Logo /></div>
        <XCircle className="w-14 h-14 text-muted-foreground mx-auto mb-4" />
        <h1 className="font-serif text-2xl font-semibold">Checkout cancelled</h1>
        <p className="text-sm text-muted-foreground mt-2">No payment was taken. You can pick a plan whenever you're ready.</p>
        <Link to="/app/billing"><Button className="rounded-none mt-6 w-full" data-testid="payment-cancel-back">Back to billing</Button></Link>
      </div>
    </div>
  );
}
